// Qué tiendas mostrar en el listado de una empresa (Stores.jsx del flujo
// global y StoresCompany.jsx del flujo /company). Módulo puro — sin React,
// Zustand ni Sentry, mismo patrón y misma prohibición de mockearlo en los
// tests de sus consumidores que `oneclickCards.js`/`mapaUbicaciones.js`.
//
// El problema que resuelve: `storeByDistance` filtra por un radio
// (`RadioTiendas` en simadb.miscelaneos, 5 km por defecto), así que una
// empresa sin máquinas dentro de ese radio devuelve una lista vacía y el
// listado quedaba en un callejón sin salida — incluso cuando el usuario tenía
// favoritas guardadas de esa misma empresa, que es justamente lo que el Home
// ofrece en "Mis tiendas". La lógica vive acá (y no duplicada en las dos
// páginas) porque ya nos pasó que dos copias del mismo componente divergieran
// y un fix aterrizara solo en una (ver ADR-0013).

// Los ids llegan de endpoints distintos y persistidos en localStorage, así que
// se comparan siempre normalizados: `storeByDistance` y el padrón pueden
// diferir en tipo (number vs string) para el mismo id, y una comparación
// estricta marcaría TODAS las favoritas como borradas, reinstalando el
// callejón sin salida que este módulo existe para evitar.
const mismoId = (a, b) => Number(a) === Number(b);

// `vende_licor` NO viene en todos los endpoints: `lista-producto-buscado`
// (Featured) lo omite, y desde ahí también se pueden marcar favoritas
// (ModalProductDetail). Por eso "vende licor" es sólo el caso positivo
// explícito (1 / "1" / true); ausente o no reconocible se trata como que no
// vende, en vez de descartar la tienda. Con la comparación estricta anterior
// (`vende_licor === 0`) una favorita sin ese campo desaparecía del listado y
// el aviso quedaba sobre una lista vacía.
export const vendeLicor = (tienda) => {
  const v = tienda?.vende_licor;
  if (v === true) return true;
  return Number(v) === 1;
};

export const filtrarSinLicor = (tiendas) =>
  Array.isArray(tiendas) ? tiendas.filter((t) => !vendeLicor(t)) : [];

// "¿Esta favorita todavía existe?" es una pregunta DISTINTA de "¿está cerca
// de mí?". Se valida contra el padrón de máquinas activas
// (`/apiV2/maquina/listaMaquinas`, que filtra por estado pero no por
// distancia), no contra la respuesta de `storeByDistance`: validarla contra
// esa respuesta ataba ambas preguntas y hacía que una favorita fuera del
// radio fuera indistinguible de una borrada, así que no podía mostrarse nunca.
//
// Con el padrón vacío (todavía no cargó, o el endpoint devolvió algo que no es
// un array — este backend puede responder 200 con {status:"error"}) NO se
// descarta ninguna favorita: es mejor mostrarlas que vaciar la pantalla por un
// fallo de red ajeno.
export const filtrarFavoritasVigentes = (favoritas, maquinasActivas) => {
  if (!Array.isArray(favoritas)) return [];
  if (!Array.isArray(maquinasActivas) || maquinasActivas.length === 0) {
    return favoritas;
  }
  return favoritas.filter((fav) =>
    maquinasActivas.some((m) => mismoId(m?.maquina_id, fav?.maquina_id)),
  );
};

// Viendo una empresa puntual, sólo sus favoritas. `tiendaId` 0 (o ausente)
// significa "todas las empresas", así que no se acota.
export const filtrarFavoritasPorEmpresa = (favoritas, tiendaId) => {
  if (!Array.isArray(favoritas)) return [];
  const id = Number(tiendaId);
  if (!(id > 0)) return favoritas;
  return favoritas.filter((fav) => mismoId(fav?.empresa_id, id));
};

/**
 * @param estadoCercanas "cargando" | "ok" | "error" — estado de la consulta a
 *   `storeByDistance`. Es imprescindible distinguirlos: esa query es
 *   `enabled:false` y se dispara desde un efecto, así que en cada montaje pasa
 *   por un momento con `data` undefined (lista vacía), y con `retry:false` un
 *   error la deja vacía para siempre. Sin este estado, "no cargó" era
 *   indistinguible de "no hay ninguna cerca" y la UI afirmaba lo segundo.
 * @returns motivoFallback null | "sin-cercanas" | "error" — por qué se están
 *   mostrando las favoritas, para que la UI elija un texto que sea verdad.
 */
export const resolverTiendasVisibles = ({
  favoritas = [],
  maquinasActivas = [],
  maquinasCercanas = [],
  tiendaId = 0,
  mostrarFavoritas = false,
  estadoCercanas = "ok",
}) => {
  const cercanas = Array.isArray(maquinasCercanas) ? maquinasCercanas : [];
  const favoritasDelContexto = filtrarFavoritasPorEmpresa(
    filtrarFavoritasVigentes(favoritas, maquinasActivas),
    tiendaId,
  );

  // La pestaña de favoritas es una elección explícita del usuario: nunca se
  // considera "fallback" ni lleva aviso.
  if (mostrarFavoritas) {
    return {
      favoritasDelContexto,
      tiendasAMostrar: favoritasDelContexto,
      motivoFallback: null,
    };
  }

  // Si hay aunque sea una tienda dentro del radio, manda la respuesta del
  // backend: es la lista real ordenada por distancia.
  if (cercanas.length > 0) {
    return { favoritasDelContexto, tiendasAMostrar: cercanas, motivoFallback: null };
  }

  // Sin nada cerca se cae a las favoritas (mejor que un callejón sin salida),
  // pero el aviso sólo se muestra cuando de verdad sabemos que no hay ninguna
  // cerca: mientras carga no se afirma nada, y si falló se dice que falló.
  const motivoFallback =
    favoritasDelContexto.length === 0
      ? null
      : estadoCercanas === "error"
        ? "error"
        : estadoCercanas === "ok"
          ? "sin-cercanas"
          : null; // "cargando": se muestran, sin afirmar nada todavía

  return {
    favoritasDelContexto,
    tiendasAMostrar: favoritasDelContexto,
    motivoFallback,
  };
};

// Copys centralizados para que las dos páginas digan lo mismo, y para que
// ninguno afirme algo que no se verificó. El texto del fallback y el del
// estado vacío distinguen "entré a una empresa" de "estoy viendo todas":
// decir "de esta empresa" en el listado general es falso, porque ahí la lista
// mezcla empresas.
export const textoFallbackFavoritas = ({ motivoFallback, tiendaId }) => {
  if (!motivoFallback) return null;
  if (motivoFallback === "error") {
    return "No pudimos cargar las tiendas cercanas. Estas son las que marcaste como favoritas.";
  }
  return Number(tiendaId) > 0
    ? "No hay tiendas de esta empresa cerca de tu ubicación. Estas son las que marcaste como favoritas."
    : "No hay tiendas cerca de tu ubicación. Estas son las que marcaste como favoritas.";
};

export const textoListadoVacio = ({ tiendaId, estadoCercanas = "ok" }) => {
  if (estadoCercanas === "error") {
    return "No pudimos cargar las tiendas cercanas. Reintenta en unos segundos.";
  }
  return Number(tiendaId) > 0
    ? "Esta empresa no tiene tiendas cerca de tu ubicación, y no tienes tiendas suyas marcadas como favoritas."
    : "No hay tiendas cercanas a tu ubicación.";
};

export const TIENDAS_MSG_SIN_FAVORITAS_EMPRESA =
  "Todavía no marcaste tiendas favoritas de esta empresa.";

export const TIENDAS_MSG_SIN_FAVORITAS_GENERAL =
  "Todavía no marcaste ninguna tienda como favorita.";
